import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  StRoomPoolDetail,
  StRoomPoolListItem,
  StRoomPoolMember,
  stErrorMessage,
  stRoomPool,
} from '../../../services/socialTradingService';
import { StNameIdCell, StScrollTable, stText } from '../StListUi';
import { memberRewardSources, rewardSourceKindKey } from './helpers';

type Props = {
  pool: StRoomPoolListItem;
  onClose(): void;
};

/** The per-source split of one member's payout, or the single legacy total. */
function RewardCell({ member }: { member: StRoomPoolMember }) {
  const t = useTranslate();
  const sources = memberRewardSources(member);

  if (!sources) {
    // Settled before V2.5: the whole payout is the total, with no breakdown.
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2">{stText(member.estReward)}</Typography>
        <Typography variant="caption" color="text.disabled">
          {t('module.socialTrading.rewardSourcesLegacy')}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{stText(member.estReward)}</Typography>
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }} useFlexGap>
        {sources.map((source) => (
          <Chip
            key={source.kind}
            size="small"
            variant="outlined"
            color={source.belowThreshold ? 'default' : 'success'}
            label={`${t(rewardSourceKindKey(source.kind), { _: source.kind })}: ${source.amount}`}
            // A zero amount is kept and explained rather than hidden.
            title={source.belowThreshold ? t('module.socialTrading.rewardSourceBelow') : undefined}
          />
        ))}
      </Stack>
    </Stack>
  );
}

/** Read-only leaderboard of a room's current cycle. */
export function MembersDialog({ pool, onClose }: Props) {
  const t = useTranslate();
  const [detail, setDetail] = useState<StRoomPoolDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await stRoomPool(pool.roomId));
    } catch (e: any) {
      setError(stErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [pool.roomId]);

  useEffect(() => {
    load();
  }, [load]);

  const members = detail?.members ?? [];

  return (
    <>
      <DialogTitle>
        {t('module.socialTrading.poolMembers')} — {pool.roomName}
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && members.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('module.socialTrading.poolMembersEmpty')}
          </Typography>
        )}

        {members.length > 0 && (
          <StScrollTable minWidth={760}>
            <TableHead>
              <TableRow>
                <TableCell>{t('module.socialTrading.colRank')}</TableCell>
                <TableCell>{t('module.socialTrading.colUser')}</TableCell>
                <TableCell>{t('module.socialTrading.colVolume')}</TableCell>
                <TableCell>{t('module.socialTrading.colProfit')}</TableCell>
                <TableCell>{t('module.socialTrading.colEstReward')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.userId}>
                  <TableCell>{stText(member.rank)}</TableCell>
                  <TableCell>
                    <StNameIdCell name={member.nickname} id={member.userId} />
                  </TableCell>
                  <TableCell>{stText(member.volume)}</TableCell>
                  <TableCell>{stText(member.profit)}</TableCell>
                  <TableCell>
                    <RewardCell member={member} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StScrollTable>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={load} disabled={loading}>
          {t('module.socialTrading.refresh')}
        </Button>
        <Button variant="contained" color="inherit" onClick={onClose}>
          {t('module.socialTrading.close')}
        </Button>
      </DialogActions>
    </>
  );
}
